import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { FullScreenPreviewService } from './full-screen-preview.service';
import { Subscription } from 'rxjs';
import { Content } from './full-screen-preview.service';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-full-screen-preview',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  template: `
    <div
      class="full-screen-preview"
      [ngClass]="{
        active: openPreview,
        'is-invoice': content.isInvoice,
        'is-image': !content.isHTML,
        'is-video': content.isVideo,
      }"
      (click)="closePreview()"
      (mousedown)="
        $event.target === $event.currentTarget ? closePreview() : null
      "
    >
      <div
        class="full-screen-preview-main"
        *ngIf="!destroyContent"
        (click)="
          content.isHTML || content.isVideo ? $event.stopPropagation() : null
        "
        (mousedown)="$event.stopPropagation()"
      >
        <div
          #videoContainer
          *ngIf="content.isVideo"
          class="full-screen-preview-video"
          (click)="$event.stopPropagation()"
        >
          <ng-container *ngIf="openPreview && content.isVideo">
            <video
              *ngIf="videoKey"
              #videoEl
              [src]="content.src"
              autoplay
              controls
              playsinline
            ></video>
          </ng-container>
        </div>
        <ng-container *ngIf="!content.isVideo">
          <div
            *ngIf="content.isHTML"
            class="full-screen-preview-html"
            [innerHTML]="safeHtml"
          ></div>
          <div *ngIf="!content.isHTML" class="full-screen-preview-image">
            <span *ngIf="content.showLabel" class="full-screen-preview-id">{{
              content.label
            }}</span>
            <div
              class="full-screen-preview-image-container"
              [style.background-image]="'url(' + content.src + ')'"
            ></div>
          </div>
        </ng-container>
      </div>
      <div class="full-screen-preview-close" (click)="service.closePreview()">
        <mat-icon> close </mat-icon>
      </div>
    </div>
  `,
  styleUrls: ['./full-screen-preview.component.css'],
})
export class FullScreenPreviewComponent implements OnInit, OnDestroy {
  constructor(
    private sanitizer: DomSanitizer,
    public service: FullScreenPreviewService,
  ) {}
  private subscriptions = new Subscription();

  @ViewChild('videoEl') videoEl?: ElementRef<HTMLVideoElement>;

  content: Content = {
    src: '',
    html: '',
    label: '',
    isHTML: false,
    showLabel: false,
    isVideo: false,
  };

  safeHtml: any;

  openPreview: boolean = false;
  destroyContent: boolean = false;
  activeTimeout: any;

  ngOnInit(): void {
    this.subscriptions.add(
      this.service.getContent().subscribe((content) => {
        if (content?.isVideo && content.src !== this.content?.src) {
          this.videoKey++;
        }

        this.destroyContent = false;
        this.content = content;

        if (this.content.isHTML) {
          this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(
            this.content.html,
          );
        }
      }),
    );
    this.subscriptions.add(
      this.service.getIsPreviewOpen().subscribe((isPreviewOpen) => {
        this.openPreview = isPreviewOpen;

        this.activeTimeout = setTimeout(() => {
          if (!this.openPreview) {
            this.destroyContent = true;
          }
        }, 300);

        if (isPreviewOpen) {
          document.body.classList.add('preview-open');
        } else {
          document.body.classList.remove('preview-open');
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    clearTimeout(this.activeTimeout);
  }

  videoKey = 0;

  private resetVideoElement(): void {
    const video = this.videoEl?.nativeElement;
    if (!video) return;

    try {
      video.pause();
      video.removeAttribute('src');
      video.load();
    } catch {}
  }

  closePreview() {
    this.resetVideoElement();
    this.videoKey++;

    this.service.closePreview();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (!this.openPreview) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      const target = event.target as HTMLElement;
      if (target && typeof target.blur === 'function') {
        target.blur();
      }
      this.closePreview();
    }
  }
}
